import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { api } from '../api/client';

interface User {
  id: string;
  email: string;
  full_name?: string;
  avatar_url?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, full_name?: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  exchangeSSOToken: (ssoToken: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      api.setToken(token);
      api.get('/api/me')
        .then((res) => {
          if (res.data) setUser(res.data);
        })
        .catch(() => {
          logout();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post('/api/auth/login', { email, password });
    const { token: newToken, user: newUser } = res.data;
    localStorage.setItem('token', newToken);
    api.setToken(newToken);
    setToken(newToken);
    setUser(newUser);
  }, []);

  const register = useCallback(async (email: string, password: string, full_name?: string) => {
    const res = await api.post('/api/auth/register', { email, password, full_name });
    const { token: newToken, user: newUser } = res.data;
    localStorage.setItem('token', newToken);
    api.setToken(newToken);
    setToken(newToken);
    setUser(newUser);
  }, []);

  const exchangeSSOToken = useCallback(async (ssoToken: string) => {
    const res = await api.post('/api/auth/sso/exchange', { token: ssoToken });
    const { token: newToken, user: newUser } = res.data;
    localStorage.setItem('token', newToken);
    api.setToken(newToken);
    setToken(newToken);
    setUser(newUser);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    api.setToken(null);
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, register, loading, exchangeSSOToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
